/*
 *   File : area.js
 *   Author : https://evoqins.com
 *   Description : Area chart component
 *   Integrations : 'react-chartjs-2'
 *   Version : 1.0.0
*/

// custom packages
import React from 'react';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Filler, Legend, } from 'chart.js';


ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Filler,
    Legend
);

// gradient 
const _getGradient = (ctx, chartArea) => {

    if (chartArea) {
        let width, height, gradient;
        const chartWidth = chartArea.right - chartArea.left;
        const chartHeight = chartArea.bottom - chartArea.top;
        if (gradient === null || width !== chartWidth || height !== chartHeight) {
            width = chartWidth;
            height = chartHeight;
            gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
            gradient.addColorStop(1, 'rgba(104, 41, 218, 0.25)');
            gradient.addColorStop(0, 'rgba(104, 41, 218, 0.05)');

        }
        return gradient;
    }
};

// Helper function to calculate nice tick values
const calculateNiceScale = (maxValue, tickCount = 5) => {
    // Handle edge cases
    if (maxValue <= 0) return { max: 10, stepSize: 2 };

    const range = maxValue;
    const unroundedTickSize = range / (tickCount - 1);
    const x = Math.ceil(Math.log10(unroundedTickSize) - 1);
    const pow10x = Math.pow(10, x);

    const roundedTickRange = Math.ceil(unroundedTickSize / pow10x) * pow10x;
    const max = roundedTickRange * Math.ceil(range / roundedTickRange);

    return {
        max: max,
        stepSize: roundedTickRange
    };
};

// Format currency for Indian locale
const formatCurrency = (value) => {
    if (value >= 10000000) {
        return (value / 10000000).toFixed(1).replace(/\.0$/, '') + 'C';
    } else if (value >= 100000) {
        return (value / 100000).toFixed(1).replace(/\.0$/, '') + 'L';
    } else if (value >= 1000) {
        return (value / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
    }
    return value.toString();
};

const AreaChart = (props) => {

    const dataValues = props.data.data;

    const maxYValue = Math.max(...dataValues.filter(val => val !== null && val !== undefined && !isNaN(val)));
    const { max: roundedMax, stepSize } = calculateNiceScale(maxYValue);

    const labels = props.data.label;
    const DATA = {
        labels,
        datasets: [
            {
                fill: true,
                label: 'AUM',
                data: props.data.data,
                backgroundColor: function (context) {
                    const chart = context.chart;
                    const { ctx, chartArea } = chart;
                    return _getGradient(ctx, chartArea);
                },
                borderColor: '#8873FC',
                pointRadius: 0,
                pointBackgroundColor: '#8873FC',
                pointBorderColor: '#8873FC',
                tension: 0.4,
            },
        ],
    };

    const OPTIONS = {
        plugins: {
            filler: {
                propagate: false,
            },
            title: {
                display: true,
            },
            usePointStyle: true,
            legend: {
                display: false
            },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        return 'AUM: ₹' + context.raw.toLocaleString('en-IN');
                    }
                },
                backgroundColor: '#000',
                displayColors: false,
                bodyColor: '#fff',
                paddingRight: 12,
                paddingLeft: 12,
                paddingTop: 8,
                paddingBottom: 8,
                cornerRadius: 12,
                bodyFont: {
                    family: 'Myriad Pro',
                    weight: '400',
                    size: 12
                },
                cornerRadius: 2
            }
        },
        interaction: {
            intersect: false,
        },
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            x: {
                grid: {
                    display: true,
                },
                border: {
                    display: false,
                    dash: [2, 2],

                },
                ticks: {
                    color: '#8D6AAD',
                    font: {
                        size: 12,
                        weight: 400,
                        family: 'Myriad Pro'
                    }
                }
            },
            y: {
                grid: {
                    display: true
                },
                border: {
                    display: false,
                    dash: [2, 2],

                },
                min: 0,
                max: roundedMax,
                ticks: {
                    stepSize: stepSize,
                    callback: function (value) {
                        return formatCurrency(value);
                    },
                    color: '#8D6AAD',
                    font: {
                        size: 12,
                        weight: 400,
                        family: 'Myriad Pro'
                    }
                }
            },
        },
    };



    return (
        <div className='chart-wrapper d-flex flex-column h-100 w-100'>
            <Line id="my-tooltip-1"
                options={OPTIONS}
                data={DATA} />
        </div>
    )
}

export default AreaChart;